import { useEffect } from "react";

const CANONICAL_ROOT = "https://leaseflow.uk";
const DEFAULT_OG_IMAGE = `${CANONICAL_ROOT}/og-default.png`;

/**
 * Per-page <head> manager for marketing pages.
 *
 *   <SEO
 *     title="Pricing — LeaseFlow"
 *     description="Three plans for UK conveyancing firms..."
 *     path="/pricing"
 *   />
 *
 * Defaults:
 *   - canonical = CANONICAL_ROOT + path
 *   - og:url    = canonical
 *   - og:image  = DEFAULT_OG_IMAGE
 *   - twitter:card = summary_large_image
 *   - robots    = "index, follow"
 *
 * Pass `noIndex` for auth / private screens.
 */
export default function SEO({
  title,
  description,
  path = "/",
  image,
  noIndex = false,
  type = "website",
}) {
  const canonical = `${CANONICAL_ROOT}${path === "/" ? "" : path}`;
  const ogImage = image || DEFAULT_OG_IMAGE;
  const robots = noIndex ? "noindex, nofollow" : "index, follow";

  useEffect(() => {
    if (title) document.title = title;

    const tags = [
      { kind: "name",     key: "description",        value: description },
      { kind: "name",     key: "robots",             value: robots },
      { kind: "property", key: "og:type",            value: type },
      { kind: "property", key: "og:url",             value: canonical },
      { kind: "property", key: "og:title",           value: title },
      { kind: "property", key: "og:description",     value: description },
      { kind: "property", key: "og:image",           value: ogImage },
      { kind: "property", key: "og:site_name",       value: "LeaseFlow" },
      { kind: "property", key: "og:locale",          value: "en_GB" },
      { kind: "name",     key: "twitter:card",       value: "summary_large_image" },
      { kind: "name",     key: "twitter:url",        value: canonical },
      { kind: "name",     key: "twitter:title",      value: title },
      { kind: "name",     key: "twitter:description", value: description },
      { kind: "name",     key: "twitter:image",      value: ogImage },
    ];

    tags.forEach(({ kind, key, value }) => {
      if (!value) return;
      const selector = `meta[${kind}="${key}"]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(kind, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    });

    // Canonical link — replace
    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute("href", canonical);
  }, [title, description, canonical, ogImage, robots, type]);

  return null;
}
